import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  sendMessage, 
  getMessages, 
  immortalizeMessage,
  startMessagePolling,
  stopMessagePolling,
  createImmortalSignMessage,
  ChatMessage,
  SendMessageRequest,
} from '../services/chat';

export interface UseTeamChatOptions {
  teamId: number;
  userAddress?: string;
  userName?: string;
  autoConnect?: boolean;
  pollInterval?: number;
}

export interface UseTeamChatReturn {
  // Messages
  messages: ChatMessage[];
  
  // Send message
  send: (content: string) => Promise<boolean>;
  
  // Immortalize a message (requires signing)
  immortalize: (content: string, signMessage: (msg: string) => Promise<string>) => Promise<boolean>;
  
  // State
  loading: boolean;
  connected: boolean;
  error: string | null;
  
  // Actions
  connect: () => void;
  disconnect: () => void;
  refresh: () => Promise<void>;
}

/**
 * Hook for team chat functionality
 */
export function useTeamChat(options: UseTeamChatOptions): UseTeamChatReturn {
  const { 
    teamId, 
    userAddress, 
    userName,
    autoConnect = true,
    pollInterval = 5000,
  } = options;
  
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const teamIdRef = useRef(teamId);
  
  // Update ref when teamId changes
  useEffect(() => {
    teamIdRef.current = teamId;
  }, [teamId]);

  // Handle new messages from polling
  const handleNewMessages = useCallback((newMessages: ChatMessage[]) => {
    setMessages(prev => {
      // Merge messages, avoiding duplicates
      const existingIds = new Set(prev.map(m => m.id));
      const uniqueNew = newMessages.filter(m => !existingIds.has(m.id));
      return [...prev, ...uniqueNew].sort((a, b) => a.timestamp - b.timestamp);
    });
  }, []);

  // Connect to chat (start polling)
  const connect = useCallback(() => {
    setConnected(true);
    setError(null);
    startMessagePolling(teamIdRef.current, handleNewMessages, pollInterval);
  }, [handleNewMessages, pollInterval]);

  // Disconnect from chat (stop polling)
  const disconnect = useCallback(() => {
    setConnected(false);
    stopMessagePolling();
  }, []);

  // Refresh messages manually
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedMessages = await getMessages(teamIdRef.current, 0, 100);
      setMessages(fetchedMessages);
      setError(null);
    } catch (err) {
      setError('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  }, []);

  // Send a message
  const send = useCallback(async (content: string): Promise<boolean> => {
    if (!userAddress) {
      setError('Wallet not connected');
      return false;
    }

    setLoading(true);
    try {
      const request: SendMessageRequest = {
        teamId: teamIdRef.current,
        content,
        sender: userAddress,
        senderName: userName,
      };

      const result = await sendMessage(request);
      
      if (!result.success) {
        setError(result.error || 'Failed to send message');
        return false;
      }

      // Refresh to get the new message
      await refresh();
      return true;
    } catch (err) {
      setError('Failed to send message');
      return false;
    } finally {
      setLoading(false);
    }
  }, [userAddress, userName, refresh]);

  // Immortalize a message (requires wallet signature)
  const immortalize = useCallback(async (
    content: string,
    signMessage: (msg: string) => Promise<string>
  ): Promise<boolean> => {
    if (!userAddress) {
      setError('Wallet not connected');
      return false;
    }

    setLoading(true);
    try {
      // Create the message to sign
      const messageToSign = createImmortalSignMessage(content, teamIdRef.current);
      
      // Get signature from wallet
      const signature = await signMessage(messageToSign);
      
      // Send immortal message
      const result = await immortalizeMessage({
        teamId: teamIdRef.current,
        content,
        sender: userAddress,
        senderName: userName,
        signature,
        message: messageToSign,
      });

      if (!result.success) {
        setError(result.error || 'Failed to immortalize message');
        return false;
      }

      return true;
    } catch (err) {
      setError('Failed to immortalize message');
      return false;
    } finally {
      setLoading(false);
    }
  }, [userAddress, userName]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  // Reset when team changes
  useEffect(() => {
    setMessages([]);
    if (connected) {
      disconnect();
      connect();
    }
  }, [teamId]);

  return {
    messages,
    send,
    immortalize,
    loading,
    connected,
    error,
    connect,
    disconnect,
    refresh,
  };
}
